// Panel de la página Configuración. Extraído de pages/Configuracion.tsx
// (modularización 2026-06-11) sin cambios de comportamiento.
import { type FormEvent, useEffect, useState } from "react";
import { ApiError, api, type TipoFalla } from "@/lib/api";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { SkeletonRows } from "@/ui/Skeleton";
import { useToast } from "@/ui/Toast";

// ============================================================================
// TIPOS DE SOLICITUD
// ============================================================================

export default function TiposFallaPanel() {
  const toast = useToast();
  const [tipos, setTipos] = useState<TipoFalla[]>([]);
  const [loading, setLoading] = useState(true);
  const [nombreNuevo, setNombreNuevo] = useState("");
  const [creando, setCreando] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [editNombre, setEditNombre] = useState("");
  const [confirmDel, setConfirmDel] = useState<TipoFalla | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const list = await api.get<TipoFalla[]>(
        "/api/tipos-falla?incluir_inactivos=true",
      );
      setTipos(list);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function crear(e: FormEvent) {
    e.preventDefault();
    if (!nombreNuevo.trim()) return;
    setCreando(true);
    try {
      const t = await api.post<TipoFalla>("/api/tipos-falla", {
        nombre: nombreNuevo.trim(),
      });
      toast.success("Tipo creado", t.nombre);
      setNombreNuevo("");
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo crear", msg);
    } finally {
      setCreando(false);
    }
  }

  async function toggleActivo(t: TipoFalla) {
    try {
      await api.patch(`/api/tipos-falla/${t.id}`, { activo: !t.activo });
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    }
  }

  function startEdit(t: TipoFalla) {
    setEditandoId(t.id);
    setEditNombre(t.nombre);
  }

  async function saveEdit(t: TipoFalla) {
    if (!editNombre.trim() || editNombre === t.nombre) {
      setEditandoId(null);
      return;
    }
    try {
      await api.patch(`/api/tipos-falla/${t.id}`, { nombre: editNombre.trim() });
      toast.success("Tipo renombrado");
      setEditandoId(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo renombrar", msg);
    }
  }

  async function eliminar(t: TipoFalla) {
    try {
      await api.del(`/api/tipos-falla/${t.id}`);
      toast.success("Tipo eliminado", t.nombre);
      await refresh();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo eliminar", msg);
    }
  }

  return (
    <>
      <form onSubmit={crear} className="card flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="text-xs uppercase tracking-wide text-zinc-400">
            Nuevo tipo de falla
          </label>
          <input
            type="text"
            className="input mt-1"
            value={nombreNuevo}
            onChange={(e) => setNombreNuevo(e.target.value)}
            placeholder="ej. Actualización de FW, Cambio de APN, Configuración inicial"
          />
        </div>
        <button
          type="submit"
          disabled={!nombreNuevo.trim() || creando}
          className="btn-primary disabled:opacity-50"
        >
          {creando ? "Creando..." : "+ Agregar"}
        </button>
      </form>

      <div className="card">
        <div className="text-xs text-zinc-500 mb-3">
          Solo aplica cuando un caso es categoría "Falla". El nombre del
          tipo seleccionado se guarda en el campo "Detalle" del caso.
        </div>
        {loading ? (
          <SkeletonRows rows={4} cols={2} />
        ) : tipos.length === 0 ? (
          <p className="text-sm text-zinc-500">
            No hay tipos todavía. Agrega el primero arriba.
          </p>
        ) : (
          <ul className="divide-y divide-ink-700">
            {tipos.map((t) => (
              <li
                key={t.id}
                className={`py-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between ${t.activo ? "" : "opacity-50"}`}
              >
                {editandoId === t.id ? (
                  <input
                    type="text"
                    autoFocus
                    className="input flex-1"
                    value={editNombre}
                    onChange={(e) => setEditNombre(e.target.value)}
                    onBlur={() => saveEdit(t)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit(t);
                      if (e.key === "Escape") setEditandoId(null);
                    }}
                  />
                ) : (
                  <span className="text-zinc-200 flex-1">{t.nombre}</span>
                )}
                <div className="flex flex-wrap gap-1 sm:flex-shrink-0">
                  <button onClick={() => toggleActivo(t)} className="btn-ghost text-xs">
                    {t.activo ? "Desactivar" : "Activar"}
                  </button>
                  <button onClick={() => startEdit(t)} className="btn-ghost text-xs">
                    Renombrar
                  </button>
                  <button
                    onClick={() => setConfirmDel(t)}
                    className="btn-ghost text-xs text-red-400 hover:text-red-300"
                  >
                    Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmDel && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDel(null)}
          title={`Eliminar "${confirmDel.nombre}"`}
          description="Los casos viejos que tenían este tipo conservan el texto en su detalle (no se modifican)."
          variant="danger"
          confirmLabel="Eliminar"
          onConfirm={() => eliminar(confirmDel)}
        />
      )}
    </>
  );
}
